/**
 * Compress an image file to ensure it's under 200KB while maintaining reasonable quality
 * @param {File} file - The image file to compress
 * @param {number} maxSizeKB - Maximum file size in KB (default: 200)
 * @param {number} maxWidth - Maximum width in pixels (default: 800)
 * @param {number} maxHeight - Maximum height in pixels (default: 800)
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file, maxSizeKB = 200, maxWidth = 800, maxHeight = 800) => {
  return new Promise((resolve, reject) => {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      reject(new Error('File is not an image'));
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = calculateDimensions(img.width, img.height, maxWidth, maxHeight);
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw the image on canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // Start compression with high quality
        compressWithQuality(canvas, file.name, file.type, maxSizeKB, 0.9, resolve, reject);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Create object URL for the image
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;
    
    // Clean up object URL after loading
    img.onload = (originalOnload => {
      return function() {
        URL.revokeObjectURL(objectUrl);
        originalOnload.call(this);
      };
    })(img.onload);
  });
};

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
const calculateDimensions = (originalWidth, originalHeight, maxWidth, maxHeight) => {
  let width = originalWidth;
  let height = originalHeight;

  // Scale down if image is larger than max dimensions
  if (width > maxWidth || height > maxHeight) {
    const ratio = Math.min(maxWidth / width, maxHeight / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  return { width, height };
};

/**
 * Compress image with specific quality until size target is met
 */
const compressWithQuality = (canvas, fileName, fileType, maxSizeKB, quality, resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Failed to create compressed image'));
      return;
    }

    const fileSizeKB = blob.size / 1024;
    
    // If size is acceptable, return the compressed file
    if (fileSizeKB <= maxSizeKB || quality <= 0.1) {
      const compressedFile = new File([blob], fileName, {
        type: fileType,
        lastModified: Date.now(),
      });
      resolve(compressedFile);
      return;
    }

    // If still too large, try with lower quality
    const newQuality = Math.max(0.1, quality - 0.1);
    
    // If quality is already very low, try reducing dimensions
    if (quality <= 0.3) {
      const newWidth = Math.round(canvas.width * 0.9);
      const newHeight = Math.round(canvas.height * 0.9);
      
      if (newWidth < 100 || newHeight < 100) {
        // Image is too small, accept current result
        const compressedFile = new File([blob], fileName, {
          type: fileType,
          lastModified: Date.now(),
        });
        resolve(compressedFile);
        return;
      }
      
      // Resize canvas and try again
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      canvas.width = newWidth;
      canvas.height = newHeight;
      
      // Create temporary canvas to redraw at new size
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = imageData.width;
      tempCanvas.height = imageData.height;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.putImageData(imageData, 0, 0);
      
      ctx.drawImage(tempCanvas, 0, 0, newWidth, newHeight);
    }
    
    // Try compression with lower quality
    compressWithQuality(canvas, fileName, fileType, maxSizeKB, newQuality, resolve, reject);
  }, fileType, quality);
};

/**
 * Get formatted file size string
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Check if image compression is supported by the browser
 * @returns {boolean} - True if compression is supported
 */
export const isCompressionSupported = () => {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext && canvas.getContext('2d') && canvas.toBlob);
  } catch (error) {
    return false;
  }
};